import { MutableRefObject, useState } from 'react';

// Define the type for fetchData function
interface FetchDataResponse<T> {
  list: T[];
  total: number;
}

interface UseInfiniteScrollProps<T> {
  fetchData: (seed: number, count: number, searchText: string) => Promise<FetchDataResponse<T>>;
  LIMIT: number;
  ref?: MutableRefObject<HTMLElement | null> | null;
}

export default function useInfiniteScroll<T>({
  fetchData,
  LIMIT,
  ref = null
}: UseInfiniteScrollProps<T>) {
  const [list, setList] = useState<T[]>([]); // Type the list as an array of generic T
  const [total, setTotal] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [seed, setSeed] = useState<number>(0);
  const [searchText, setSearchText] = useState<string>('');

  const fetchFn = async (s = seed, count = LIMIT, searchValue = searchText) => {
    if (!loading || (loading && list.length === 0)) {
      setLoading(true);
      const res = await fetchData(s, count, searchValue);
      if (res?.list?.length > 0) {
        await Promise.resolve()
          .then(() => {
            setTotal(res.total);
          })
          .then(() => {
            setSeed(s + LIMIT);
          })
          .then(() => {
            setList((x) => {
              if (s === 0) {
                return res.list;
              }
              return [...x, ...res.list];
            });
            setLoading(false);
            setTimeout(() => {
              if (ref?.current && s !== 0) {
                ref.current.scrollBy({ top: 100, behavior: 'smooth' });
              }
            }, 200);
          });
      } else if (s === 0 && res?.list?.length === 0) {
        setTotal(0);
        setSeed(s);
        setList([]);
        setLoading(false);
      }
      setLoading(false);
    }
  };

  const scroll = (event: React.UIEvent<HTMLElement>) => {
    const listboxNode = event.currentTarget;
    if (Number(listboxNode.scrollTop + listboxNode.clientHeight) >= listboxNode.scrollHeight - 10) {
      if (list?.length < total) {
        fetchFn(seed, LIMIT, searchText);
      }
    }
  };

  return { scroll, setSearchText, total, fetchFn, list, loading };
}
